import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { Bot, RefreshCcw, Loader2 } from 'lucide-react';

/**
 * Live AI trader commentary powered by Claude Sonnet 4.5. 2-3 Turkish sentences.
 * Cached server-side (2 min). "force" refresh button bypasses cache.
 */
export default function AICommentaryCard({ symbol }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const current = useRef(symbol);

    const fetchCommentary = useCallback(async (force = false) => {
        if (!symbol) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await apiClient.get(`/ai/commentary/${symbol}`, { params: force ? { force: true } : {} });
            if (current.current !== symbol) return;
            setData(data);
        } catch (e) {
            logger.warn('AI commentary fetch failed', e);
            if (current.current === symbol) setError(e?.response?.data?.detail || 'AI yorumu alınamadı');
        } finally {
            if (current.current === symbol) setLoading(false);
        }
    }, [symbol]);

    useEffect(() => {
        current.current = symbol;
        setData(null);
        setError(null);
        if (symbol) fetchCommentary(false);
    }, [symbol, fetchCommentary]);

    if (!symbol) return null;

    const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
    const ageMinutes = generatedAt ? Math.max(0, Math.floor((Date.now() - generatedAt.getTime()) / 60000)) : null;

    return (
        <div
            className="border border-violet-500/20 bg-gradient-to-br from-violet-950/20 via-slate-900 to-slate-800 rounded-md p-4"
            data-testid="ai-commentary-card"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="h-7 w-7 rounded-sm border border-violet-500/30 bg-violet-500/10 flex items-center justify-center">
                        <Bot className="h-3.5 w-3.5 text-violet-300" />
                    </div>
                    <div>
                        <div className="text-xs font-semibold tracking-tight text-violet-100">AI Trader Yorumcu</div>
                        <div className="text-[10px] text-violet-400/60 uppercase tracking-wider">
                            Claude Sonnet 4.5
                            {data?.cached && <span className="ml-2 text-slate-400">· önbellekten</span>}
                            {ageMinutes !== null && !data?.cached && (
                                <span className="ml-2 text-slate-400">· {ageMinutes === 0 ? 'şimdi' : `${ageMinutes}dk önce`}</span>
                            )}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => fetchCommentary(true)}
                    disabled={loading}
                    data-testid="ai-commentary-refresh"
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-700 hover:border-violet-500/50 hover:bg-violet-500/5 text-slate-300 hover:text-violet-300 disabled:opacity-50 flex items-center gap-1"
                >
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
                    Yenile
                </button>
            </div>

            {loading && !data && (
                <div className="text-[12px] text-slate-400 italic">AI yorum oluşturuluyor…</div>
            )}
            {error && !data && (
                <div className="text-[11px] text-rose-400">{error}</div>
            )}
            {data?.text && (
                <div
                    data-testid="ai-commentary-text"
                    className="text-[12px] text-slate-100 leading-relaxed whitespace-pre-wrap"
                >
                    {data.text}
                </div>
            )}
            <div className="mt-3 pt-2 border-t border-violet-500/10 text-[9px] text-slate-400 uppercase tracking-widest">
                Yatırım tavsiyesi değildir · AI yorumu olasılık/risk çerçevesindedir
            </div>
        </div>
    );
}
